import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Shield, Lock, Eye, EyeOff, CheckCircle2, 
  AlertCircle, Loader2, Building2, ArrowRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AtivarConta = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  // States
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  
  // Invite data
  const [inviteData, setInviteData] = useState(null);
  
  // Form data
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Token de convite não fornecido. Verifique o link do email.');
      setValidating(false);
      setLoading(false);
      return;
    }
    
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/invite/validate/${token}`);
      setInviteData(response.data);
      setValidating(false);
      setLoading(false);
    } catch (err) {
      console.error('Token validation error:', err);
      const message = err.response?.data?.detail || 'Erro ao validar convite';
      setError(message);
      setValidating(false);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/auth/invite/activate`, {
        token,
        password,
        confirm_password: confirmPassword
      });
      
      if (response.data.success) {
        setSuccess(true);
        toast.success('Conta ativada com sucesso!');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      console.error('Activation error:', err);
      const message = err.response?.data?.detail || 'Erro ao ativar conta';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading || validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e3a5f] via-[#0d9488] to-[#1e3a5f] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#0d9488] mb-4" />
            <p className="text-slate-600">Validando seu convite...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e3a5f] via-[#0d9488] to-[#1e3a5f] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Convite Inválido</h2>
            <p className="text-slate-600 mb-6">{error}</p>
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                Se o convite expirou, solicite um novo ao RH da sua empresa.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Ir para o Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1e3a5f] via-[#0d9488] to-[#1e3a5f] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Conta Ativada!</h2>
            <p className="text-slate-600 mb-4">
              Sua conta foi ativada com sucesso. Você será redirecionado para o login em instantes...
            </p>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-emerald-700">
                <strong>Email:</strong> {inviteData?.email}
              </p>
            </div>
            <Link to="/login">
              <Button className="w-full bg-[#1e3a5f] hover:bg-[#0d9488]">
                <ArrowRight className="mr-2" size={18} />
                Ir para o Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Activation form
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1e3a5f] via-[#0d9488] to-[#1e3a5f] flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="activate-account-page">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <div className="flex items-center gap-2">
              <Shield className="text-[#0d9488]" size={40} />
              <span className="text-2xl font-bold text-[#1e3a5f]">CERP Brasil</span>
            </div>
          </div>
          
          <CardTitle className="text-xl">Ativar Sua Conta</CardTitle>
          <CardDescription>
            Crie sua senha para acessar o portal
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Welcome Message */}
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 text-center">
            <p className="text-lg font-semibold text-[#1e3a5f] mb-1">
              Olá, {inviteData?.nome}! 👋
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
              <Building2 size={14} />
              <span>{inviteData?.company_name}</span>
            </div>
          </div>

          {/* Email display */}
          <div className="bg-slate-50 rounded-lg p-3">
            <Label className="text-xs text-slate-500">Seu email</Label>
            <p className="font-medium text-slate-900">{inviteData?.email}</p>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="flex items-center gap-2">
                <Lock size={14} />
                Criar Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  minLength={6}
                  className="pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="flex items-center gap-2">
                <Lock size={14} />
                Confirmar Senha
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Digite novamente"
                  required
                  className="pr-10"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {/* Password requirements */}
            <div className="text-xs text-slate-500 space-y-1">
              <p className={`flex items-center gap-1 ${password.length >= 6 ? 'text-green-600' : ''}`}>
                {password.length >= 6 ? <CheckCircle2 size={12} /> : <span className="w-3 h-3 rounded-full border border-slate-300 inline-block" />}
                Mínimo 6 caracteres
              </p>
              <p className={`flex items-center gap-1 ${password && password === confirmPassword ? 'text-green-600' : ''}`}>
                {password && password === confirmPassword ? <CheckCircle2 size={12} /> : <span className="w-3 h-3 rounded-full border border-slate-300 inline-block" />}
                Senhas coincidem
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#0d9488] hover:bg-[#0f766e] text-lg py-6"
              disabled={submitting || password.length < 6 || password !== confirmPassword}
              data-testid="activate-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={20} />
                  Ativando...
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2" size={20} />
                  Ativar Minha Conta
                </>
              )}
            </Button>
          </form>

          {/* Help text */}
          <p className="text-xs text-center text-slate-500">
            Ao ativar sua conta, você concorda com os{' '}
            <Link to="/termos" className="text-[#0d9488] hover:underline">Termos de Uso</Link>
            {' '}e{' '}
            <Link to="/privacidade" className="text-[#0d9488] hover:underline">Política de Privacidade</Link>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AtivarConta;
